import { Checkbox, Divider, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from '@mui/material'
import { FormStyled } from 'fwork.react.apptemplate'
import { FormStyledProps } from 'fwork.react.apptemplate/src/components/forms'
import React, { useState, useEffect } from 'react'
import { CtrlParamsItemComponent } from './item'
import { CtrolType, ICtrl, ICtrlList, ICtrlParam, ICtrlRadio, ICtrlRadioDirection, IParam } from './models'
import { CtrlParamsSelectComponent } from './select'
import { DateTimePicker, LocalizationProvider, renderTimeViewClock } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'

export const ctrlParamsComponentValidateParams = (ctrls: ICtrl[], params: IParam[]) => {
  if (ctrls.length) {
    const requiredWithoutValue = ctrls.some(c => {
      const hasParam = params.some(p => p.paramName == (c as ICtrlParam).paramName && p.value)
      return (c as ICtrlParam).required && !hasParam
    })

    return !requiredWithoutValue
  }
  else
    return true
}

export const ctrlParamsComponentGetInitParams = (ctrls: ICtrl[]) => {
  const paramsTmp: IParam[] = []
  for (const c of ctrls.filter(c => c.type == CtrolType.select)) {
    if ((c as ICtrlList).items.length) {
      paramsTmp.push({
        paramName: (c as ICtrlParam).paramName!,
        value: (c as ICtrlList).items[0].value
      })
    }
  }
  return paramsTmp
}

export interface CtrlParamsComponentProps extends FormStyledProps, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  ctrls: ICtrl[],
  hover?: boolean,
  showValidateText?: boolean,
  onSelectItem?: (ctrl?: ICtrl) => void,
  onChangeParamValues?: (params: IParam[]) => void,
  onValidateParams?: (valid: boolean) => void,
}

export const CtrlParamsComponent = (props: CtrlParamsComponentProps) => {
  const { ctrls, hover, showValidateText, onSelectItem, onChangeParamValues, onValidateParams, ...rest } = props
  const [currCtrl, setCurrCtrl] = useState<ICtrl | undefined>()
  const [params, setParams] = useState<IParam[]>(ctrlParamsComponentGetInitParams(ctrls ?? []))

  const setParam = (param: IParam) => {
    const paramsTmp = [
      ...params.filter(p => p.paramName != param.paramName),
      param
    ]

    setParams(paramsTmp)

    if (onChangeParamValues) onChangeParamValues(paramsTmp)
    if (onValidateParams) onValidateParams(ctrlParamsComponentValidateParams(ctrls, paramsTmp))
  }

  const onItemClickHandle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, c: ICtrl) => {
    setCurrCtrl(c)
    if (onSelectItem)
      onSelectItem(c)
    e.stopPropagation()
  }

  const onValidateText = (ctrl: ICtrl) => {
    return showValidateText && (ctrl as ICtrlParam).required && !params.filter(p => p.paramName === (ctrl as ICtrlParam).paramName)[0]?.value ? 'Valor inválido para este campo' : null
  }

  useEffect(() => {
    setParams([])
  }, [ctrls])

  return <FormStyled
    {...rest}
    className={`${props.className}${'middleContainerComponent'}`}
    style={{ alignContent: 'flex-start', ...props.style, }}
    onClick={() => {
      setCurrCtrl(undefined)
      if (onSelectItem)
        onSelectItem(undefined)
    }}
  >
    {ctrls?.map((c, idx) => {
      return c.type == CtrolType.label ?
        // LABEL
        <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
          hover={hover}
          key={idx}
          onClick={e => {
            onItemClickHandle(e, c)
          }}>
          <div style={{ minHeight: 1 }}>{(c as ICtrlParam).label}</div>
        </CtrlParamsItemComponent> :

        // TEXT
        c.type == CtrolType.text ?
          <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
            hover={hover}
            key={idx}
            onClick={e => {
              onItemClickHandle(e, c)
            }}>
            <TextField id={c.uuid} fullWidth label={(c as ICtrlParam).label}
              helperText={onValidateText(c)}
              error={onValidateText(c) ? true : false}
              value={params.filter(p => p.paramName == (c as ICtrlParam).paramName)?.[0]?.value ?? ''}
              onChange={e => {
                if ((c as ICtrlParam).paramName)
                  setParam({
                    paramName: (c as ICtrlParam).paramName!,
                    value: e.target.value
                  })
              }}
            />
          </CtrlParamsItemComponent> :

          // NUMBER
          c.type == CtrolType.num ?
            <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
              hover={hover}
              key={idx}
              onClick={e => {
                onItemClickHandle(e, c)
              }}>
              <TextField fullWidth label={(c as ICtrlParam).label} type='number'
                helperText={onValidateText(c)}
                error={onValidateText(c) ? true : false}
                value={params.filter(p => p.paramName == (c as ICtrlParam).paramName)?.[0]?.value ?? ''}
                onChange={e => {
                  if ((c as ICtrlParam).paramName)
                    setParam({
                      paramName: (c as ICtrlParam).paramName!,
                      value: Number(e.target.value)
                    })
                }} />
            </CtrlParamsItemComponent> :

            // DATETIME
            c.type == CtrolType.date ?
              <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
                hover={hover}
                key={idx}
                onClick={e => {
                  onItemClickHandle(e, c)
                }}>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale='pt'>
                  <DateTimePicker
                    label={(c as ICtrlParam).label}
                    value={params.filter(p => p.paramName == (c as ICtrlParam).paramName)?.[0]?.value ?? null}
                    slotProps={{
                      textField: {
                        helperText: onValidateText(c),
                        error: onValidateText(c) != null ? true : false
                      }
                    }}
                    onChange={e => {
                      if ((c as ICtrlParam).paramName)
                        setParam({
                          paramName: (c as ICtrlParam).paramName!,
                          value: e
                        })
                    }}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />
                </LocalizationProvider>
              </CtrlParamsItemComponent> :

              // CHECKBOX
              c.type == CtrolType.check ?
                <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
                  hover={hover}
                  key={idx}
                  onClick={e => {
                    onItemClickHandle(e, c)
                  }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={params.filter(p => p.paramName == (c as ICtrlParam).paramName)?.[0]?.value ?? false}
                        onChange={e => {
                          if ((c as ICtrlParam).paramName)
                            setParam({
                              paramName: (c as ICtrlParam).paramName!,
                              value: e.target.checked
                            })
                        }} />
                    }
                    label={(c as ICtrlParam).label} />
                </CtrlParamsItemComponent> :

                // RADIO
                c.type == CtrolType.radio ?
                  <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
                    style={{ alignContent: 'center' }}
                    hover={hover}
                    key={idx}
                    onClick={e => {
                      onItemClickHandle(e, c)
                    }}>
                    <FormLabel id="demo-radio-buttons-group-label">{(c as ICtrlParam).label}</FormLabel>
                    <RadioGroup
                      value={params.filter(p => p.paramName == (c as ICtrlParam).paramName)?.[0]?.value ?? ''}
                      name='radio'
                      row={(c as ICtrlRadio).direction == ICtrlRadioDirection.horizontal}
                      onChange={e => {
                        if ((c as ICtrlParam).paramName)
                          setParam({
                            paramName: (c as ICtrlParam).paramName!,
                            value: e.target.value
                          })
                      }}
                    >
                      {(c as ICtrlList).items?.map((i, idx) =>
                        <FormControlLabel key={idx} value={i.value} control={<Radio />} label={i.label} />)}
                    </RadioGroup>
                  </CtrlParamsItemComponent> :

                  // SELECT
                  c.type == CtrolType.select ?
                    <CtrlParamsItemComponent selected={c.uuid == currCtrl?.uuid} gap={props.gap}
                      hover={hover}
                      key={idx}
                      onClick={e => {
                        onItemClickHandle(e, c)
                      }}>
                      <CtrlParamsSelectComponent
                        ctrl={c as ICtrlList}
                        helperText={onValidateText(c)}
                        rootProps={{
                          error: onValidateText(c) ? true : false,
                        }}
                        selectProps={{
                          value: params.filter(p => p.paramName == (c as ICtrlParam).paramName)?.[0]?.value ?? '',
                          onChange: e => {
                            if ((c as ICtrlParam).paramName)
                              setParam({
                                paramName: (c as ICtrlParam).paramName!,
                                value: e.target.value
                              })
                          }
                        }}
                      />
                    </CtrlParamsItemComponent> :

                    // DIVIDER
                    c.type == CtrolType.divider ?
                      <CtrlParamsItemComponent style={{ width: '100%' }} selected={c.uuid == currCtrl?.uuid} gap={props.gap}
                        hover={hover}
                        key={idx}
                        onClick={e => {
                          onItemClickHandle(e, c)
                        }}>
                        <Divider />
                      </CtrlParamsItemComponent> :

                      // BREAK LINE
                      <CtrlParamsItemComponent style={{ width: '100%' }} selected={c.uuid == currCtrl?.uuid} gap={props.gap}
                        hover={hover}
                        key={idx}
                        onClick={e => {
                          onItemClickHandle(e, c)
                        }}>
                        <div style={{ height: 1 }}></div>
                      </CtrlParamsItemComponent>
    })}
  </FormStyled >
}